import { Button, Input, Modal, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserProfile } from '../../context/UserProfileContext';
import SettingNav from '../../ui/layout/SettingNav';
import WorkspaceHeader from '../../ui/layout/WorkspaceHeader';
import styles from './TenantSettings.module.scss';
import CardTable from '../../components/Table/CardTable';
import NewButton from '../../components/NewButton/NewButton';
import { addUsersToTenant, createTenant, getAllTenants } from '../../infrastructure/apiClient';
import { useClients } from '../../context/ClientContext';
import { useTableState } from '../../hooks/useTableState';
import { sortData, sortConfig, type SortConfig } from '../../utils/sortUtils';

interface TenantSettingsProps {
  embedded?: boolean;
}

interface TenantRow {
  id: string;
  name: string;
  description?: string;
  policyVersion?: string;
}

const SORT_CONFIGS: SortConfig<TenantRow>[] = [
  sortConfig('name', 'string'),
  sortConfig('description', 'string'),
];

const TenantSettings = ({ embedded = false }: TenantSettingsProps) => {
  const [active, setActive] = useState<'user' | 'tenant'>('tenant');
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const { profile } = useUserProfile();
  const { refreshTenants } = useClients();
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchTenants = useCallback(async (offset: number, limit: number, signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllTenants({ offset, limit, signal });
      if (signal?.aborted) return;
      setTenants(response.data ?? []);
      updateTotal(typeof response.total === 'number' ? response.total : response.data?.length ?? 0);
    } catch (err) {
      if (signal?.aborted) return;
      const errorMessage = err instanceof Error ? err.message : 'Failed to load tenants.';
      setError(errorMessage);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  const handlePageChange = useCallback((offset: number, limit: number) => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    fetchTenants(offset, limit, controller.signal);
  }, [fetchTenants]);

  const {
    pagination,
    sorter,
    tablePagination,
    handleTableChange,
    updateTotal,
  } = useTableState<TenantRow>({ onPageChange: handlePageChange });

  const dataSource = useMemo<TenantRow[]>(() => tenants ?? [], [tenants]);

  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    fetchTenants(0, pagination.pageSize, controller.signal);
    return () => controller.abort();
  }, [fetchTenants, pagination.pageSize]);

  const handleOpenCreateModal = () => {
    setCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    if (creating) return;
    setCreateModalOpen(false);
    setCreateName('');
    setCreateDescription('');
  };

  const handleCreateTenant = async () => {
    const trimmedName = createName.trim();
    const trimmedDescription = createDescription.trim();

    if (!trimmedName) {
      message.warning('Enter a tenant name.');
      return;
    }

    if (!trimmedDescription) {
      message.warning('Enter a tenant description.');
      return;
    }

    try {
      setCreating(true);
      const newTenant = await createTenant({ name: trimmedName, description: trimmedDescription });
      const userId = profile?.id;
      if (!userId) {
        throw new Error('Unable to resolve current user.');
      }
      await addUsersToTenant(newTenant.id, [userId]);
      await refreshTenants();
      message.success('Tenant created.');
      handleCloseCreateModal();
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      await fetchTenants(0, pagination.pageSize, controller.signal);
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Unable to create tenant.');
    } finally {
      setCreating(false);
    }
  };

  const columns: ColumnsType<TenantRow> = [
    {
      title: 'Tenant Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name ?? '').localeCompare(b.name ?? ''),
      sortOrder: sorter.columnKey === 'name' ? sorter.order : null,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      sorter: (a, b) => (a.description ?? '').localeCompare(b.description ?? ''),
      sortOrder: sorter.columnKey === 'description' ? sorter.order : null,
    },
  ];

  const sortedTenants = useMemo(() => sortData(dataSource, sorter, SORT_CONFIGS), [dataSource, sorter]);

  const pagedData = sortedTenants;

  const content = (
    <div className={styles.root}>
      <div className={styles.header}>
        <h1 className={styles.title}>Tenant Dashboard</h1>
        <div className={styles.actions}>
          <NewButton label="Add new tenant" onClick={handleOpenCreateModal} />
        </div>
      </div>
      <div className={styles.tableContainer}>
        <CardTable<TenantRow>
          className={styles.cardTable}
          title="Tenants"
          columns={columns}
          dataSource={pagedData}
          rowKey={(record) => record.id}
          loading={loading}
          pagination={tablePagination}
          onChange={handleTableChange}
          error={error ?? undefined}
        />
      </div>
      <Modal
        open={createModalOpen}
        title="Create Tenant"
        onCancel={handleCloseCreateModal}
        footer={null}
        destroyOnHidden
        width={520}
        classNames={{ body: 'min-h-[220px]' }}
      >
        <div className={styles.createTenantForm}>
          <Input
            placeholder="Tenant Name"
            value={createName}
            onChange={(event) => setCreateName(event.target.value)}
            disabled={creating}
            className={styles.createTenantInput}
          />
          <Input.TextArea
            placeholder="Tenant Description"
            value={createDescription}
            onChange={(event) => setCreateDescription(event.target.value)}
            disabled={creating}
            rows={4}
            className={styles.createTenantInput}
          />
          <div className={styles.createTenantActions}>
            <Button onClick={handleCreateTenant} type="primary" loading={creating} disabled={creating}>
              Create
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className={styles.page}>
      <WorkspaceHeader />
      <div className={styles.body}>
        <aside className={styles.nav}>
          <SettingNav active={active} onChange={(key) => setActive(key as typeof active)} onHome={() => navigate('/workspace')} />
        </aside>
        <div className={styles.content}>{content}</div>
      </div>
    </div>
  );
};

export default TenantSettings;
